import { useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setUserLogged,
  setUserData,
  setLoading,
  setCorporationData,
  updateCorporationProducts,
  updateCorporationCategories,
  setComponentActive,
  updatePointPaymentMethods,
  updatePointDeliveryMethods,
  updateDiscounts,
  updateCorporationPointConfig,
  updateCorporationData,
} from "../reducers/userConfigSlice";

// UTILS
import { addIdKeyIfMissing } from "@delibrandev/utils/src/function";
import { readLoginLS } from "@delibrandev/utils";

// API
import { apiRest } from "../api/constants";
import { checkPassword, logIn, setPointConfigurations } from "../api/login";
import { getCategories, getPaymentMethods, getProducts, getShippingMethods } from "../api/commander";
import { getDiscounts, getAttributeGroups, getOptionGroups } from "../api/orders";
import { getCorporationInfo } from "../api/requests";
import { decodeToken } from "../api/utils";

const Initializer = ({ onInit }) => {
  const dispatch = useDispatch();
  const { componentActive, logged } = useSelector((state) => state.aplicationConfig.userConfig);

  // Evita inicializaciones solapadas
  const isInitializingRef = useRef(false);

  // Controla que onInit se ejecute cuando toca (sin duplicados por dobles init)
  const didInitOnceRef = useRef(false);

  // ✅ Get Corporation Data
  const initCorporation = useCallback(async () => {
    try {
      const corporationRes = await getCorporationInfo();

      if (corporationRes && !corporationRes.error) {
        dispatch(setCorporationData(corporationRes.data));
      }
    } catch (err) {
      console.error("[initCorporation] Error general al inicializar corporation:", err);
    }
  }, [dispatch]);

  // ✅ Get Session Data (catálogo, métodos, descuentos...)
  const initSession = useCallback(async () => {
    try {
      const [
        productsRes,
        categoriesRes,
        paymentMethodsRes,
        shippingMethodsRes,
        discountsRes,
        responseAttributes,
        responseOptions,
      ] = await Promise.all([
        getProducts({}),
        getCategories({}),
        getPaymentMethods(),
        getShippingMethods(),
        getDiscounts(),
        getAttributeGroups(),
        getOptionGroups(),
      ]);

      if (productsRes && !productsRes.error) {
        dispatch(updateCorporationProducts(addIdKeyIfMissing(productsRes.data.products)));
      }

      if (categoriesRes && !categoriesRes.error) {
        dispatch(updateCorporationCategories(addIdKeyIfMissing(categoriesRes.data)));
      }

      if (paymentMethodsRes && !paymentMethodsRes.error) {
        dispatch(updatePointPaymentMethods(paymentMethodsRes.data));
      }

      if (shippingMethodsRes && !shippingMethodsRes.error) {
        dispatch(updatePointDeliveryMethods(shippingMethodsRes.data));
      }

      if (discountsRes && !discountsRes.error) {
        dispatch(updateDiscounts(discountsRes?.data.filter((d) => d.isActive)));
      }
      if (responseAttributes && !responseAttributes.error) {
        dispatch(
          updateCorporationData({
            key: "attributes",
            data: responseAttributes.data,
          }),
        );
      }
      if (responseOptions && !responseOptions.error) {
        dispatch(
          updateCorporationData({
            key: "options",
            data: responseOptions.data,
          }),
        );
      }
    } catch (err) {
      console.error("[initSession] Error general al inicializar sesión:", err);
    }
  }, [dispatch]);

  /**
   * ✅ Autologin desde localStorage (solo cuando NO estamos logueados).
   * Devuelve true/false si consigue loguear.
   */
  const autoLogin = useCallback(async () => {
    try {
      const savedLogin = readLoginLS(apiRest);

      if (!(savedLogin?.password && savedLogin?.point && savedLogin?.login)) {
        dispatch(setUserLogged(false));
        return false;
      }

      // 1) Check password platform
      const password = savedLogin.password;
      const responsePassword = await checkPassword({ password });

      if (!responsePassword || responsePassword.error) {
        dispatch(setUserLogged(false));
        return false;
      }

      // 2) Select point
      const point = savedLogin.point;
      const pointResponse = await setPointConfigurations({
        pointOfSaleConfigurationId: point.PointOfSaleConfigurationId,
      });

      if (!pointResponse || pointResponse.error) {
        dispatch(setUserLogged(false));
        return false;
      }

      // 3) Login
      const loginData = savedLogin.login;
      const response = await logIn(loginData);

      if (response && !response.error) {
        dispatch(updateCorporationPointConfig(point));
        dispatch(setUserLogged(true));
        dispatch(setUserData(decodeToken(response.data.data)));
        dispatch(setComponentActive("commander"));
        return true;
      }

      dispatch(setUserLogged(false));
      return false;
    } catch {
      console.error("[autoLogin] No se pudo restaurar la sesión.");
      dispatch(setUserLogged(false));
      return false;
    }
  }, [dispatch]);

  /**
   * ✅ Inicialización controlada.
   * - Si forceAutoLogin=true, intentará autologin (solo si NO está logueado).
   * - En login manual (logged=true), NO reintenta autologin.
   */
  const initializeApp = useCallback(
    async ({ forceAutoLogin = false, callOnInit = true } = {}) => {
      // Evita solapamientos
      if (isInitializingRef.current) return;

      isInitializingRef.current = true;
      dispatch(setLoading(true));

      try {
        console.log("| 🚀 Inicializando aplicación...");

        // Autologin solo si lo pedimos y aún no está logueado
        if (forceAutoLogin && !logged) {
          await autoLogin();
        }

        await initSession();
        await initCorporation();
      } finally {
        dispatch(setLoading(false));
        isInitializingRef.current = false;

        if (callOnInit && typeof onInit === "function" && !didInitOnceRef.current) {
          didInitOnceRef.current = true;
          onInit();
        }
      }
    },
    [dispatch, initSession, initCorporation, autoLogin, logged, onInit],
  );

  // ✅ 1) Init al montar: intenta autologin y carga datos base
  useEffect(() => {
    initializeApp({ forceAutoLogin: true, callOnInit: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ 2) Cuando el usuario se loguea (login manual), recarga datos (sin autologin)
  useEffect(() => {
    if (logged) {
      // aquí no llamamos onInit de nuevo
      initializeApp({ forceAutoLogin: false, callOnInit: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logged]);

  // ✅ Scroll al cargar cada componente activo
  useEffect(() => {
    document.getElementById("adminBody")?.scrollTo(0, 0);
  }, [componentActive]);

  return null;
};

export default Initializer;
